import { useTranslation } from 'react-i18next';
import { useErrors } from 'pages/SmartConnectors/SmartConnector/hooks/useErrors';
import { FC, useContext } from 'react';
import { SmartConnectorContext } from '../../context';
import type {
  FieldLight,
  GenericOption,
  SmartConnectorBulkActionField,
} from 'pages/SmartConnectors/types';
import CustomObjectsService from 'services/CustomObjectsService';
import { v3 as uuid3 } from 'uuid';
import { useAsync } from 'react-use';
import MultiSelect from '__components/Inputs/MultiSelect';
import { getDataQAForInput } from '__components/Inputs/helpers';

export const BulkactionFieldsSelect: FC<{
  value?: SmartConnectorBulkActionField[];
  onChange: (value: SmartConnectorBulkActionField[]) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { smartConnector, metaData } = useContext(SmartConnectorContext);

  const { value: customObjectFieldOptions = [], loading } =
    useAsync(async () => {
      if (!smartConnector.custom_object) {
        return;
      }

      const { fields, categorizedFields } =
        await CustomObjectsService.getCategorizedModelFields(
          smartConnector.custom_object,
          {
            settingsSearch: true,
            transformToCamelCase: false,
          }
        );

      const systemCategoryId = uuid3(
        'sys:category:other_system_fields',
        smartConnector.custom_object
      );

      const systemCategory =
        metaData.kizen_data_seeds_system_fields && smartConnector.custom_object
          ? {
              id: systemCategoryId,
              name: metaData.kizen_data_seeds_system_fields.display_name,
              order: Infinity,
              fields: metaData.kizen_data_seeds_system_fields.fields.map(
                (field) => ({
                  id: uuid3(
                    `sys:field:${field.field_type}`,
                    smartConnector.custom_object!
                  ),
                  category: systemCategoryId,
                  ...field,
                })
              ),
            }
          : undefined;

      if (systemCategory) {
        categorizedFields.push(systemCategory);
        fields.push(...systemCategory.fields);
      }

      return categorizedFields.map(
        ({
          id,
          fields,
          name,
        }: {
          id: string;
          fields: FieldLight[];
          name: string;
        }) => ({
          options: fields.map((field) => ({
            value: field.id,
            label: field.display_name,
            data: field,
          })),
          label: name,
        })
      );
    }, [smartConnector.custom_object]);

  const { clearError, validate, error } = useErrors('bulkaction_fields');

  const handleChange = (
    value: (GenericOption & { data: SmartConnectorBulkActionField })[]
  ) => {
    onChange(value.map((item) => item.data));
    clearError();
  };

  return (
    <MultiSelect
      label={t('Choose Input Fields from Primary Object')}
      placeholder={t('Find Fields')}
      value={
        value?.map((field) => ({
          value: field.id,
          label: field.display_name,
          data: field,
        })) || []
      }
      onChange={handleChange}
      options={customObjectFieldOptions}
      isLoading={loading}
      margin
      error={error}
      validate={validate}
      {...getDataQAForInput('smart-connector-bulkaction-fields', 'dropdown')}
    />
  );
};
